import React, { useState, useEffect } from 'react';
import { Form, Input, Typography, Skeleton, Button, message, Alert, Space, Table } from 'antd';
import type { Mentor, DiscountCode, MentorshipSession, Duration } from '../../../models/Mentor';
import MentorshipAPI from '../../../services/MentorshipAPI';
import type { AxiosResponse } from 'axios';

const { Text } = Typography;

interface PaymentStepProps {
    mentor: Mentor;
    loading?: boolean;
    bookingId?: string;
    onSessionCreated: (session: MentorshipSession) => void;
}

const PaymentStep: React.FC<PaymentStepProps> = ({
    mentor,
    onSessionCreated,
}) => {
    const [form] = Form.useForm();
    const [selectedPrice, setSelectedPrice] = useState(mentor.prices?.[0] ?? null);
    const [verifyingDiscount, setVerifyingDiscount] = useState(false);
    const [discountVerified, setDiscountVerified] = useState(false);
    const [creatingMentorshipSession, setCreatingMentorshipSession] = useState(false);
    const [discountCode, setDiscountCode] = useState<DiscountCode | null>(null);
    const [mentorShipSession, setMentorShipSession] = useState<any>(null); // Replace with actual type
    const [ networkResponse, setNetworkResponse] = useState<any>(null); // Replace with actual type

    
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    // Equivalent to componentDidMount
    // Check if the user is logged in by verifying the presence of the token
    const token = localStorage.getItem('atilaToken');

    setIsLoggedIn(!!token);
  }, []); // Empty dependency array ensures this runs only once on mount

    const handleValuesChange = (changedValues: any) => {
        if (changedValues.discountCode) {
            setDiscountVerified(false);
        }
    };
    const getErrorMessage = (error: any) => {
        if (error.response && error.response.data && error.response.data.error) {
            return error.response.data.error;
        }
        return error.message || 'An unknown error occurred';
    };
    const createMentorshipSession =  async () => {
        setCreatingMentorshipSession(true);
        try {
            
            if (!discountCode) return message.error('Please enter a discount code');

            const atilaUser = localStorage.getItem("atilaUser");
            if (!atilaUser) return message.error('User not found');
            const userId = JSON.parse(atilaUser).id;
            const createMentorshipSessionResponse = await MentorshipAPI
                .createMentorshipSession({
                    mentee: userId,
                    mentor: mentor.id,
                    discountcode_set: [discountCode.id],
                    duration: selectedPrice
                });
            
            const {data: createdSession} = createMentorshipSessionResponse;
            setMentorShipSession(createdSession);
            onSessionCreated(createdSession);
            
            setNetworkResponse({title: "Session created successfully", type: "success"});
        } catch (error: any) {
            setNetworkResponse({title: "Error creating mentorship session: " +getErrorMessage(error), type: "error"});
            console.log("Error creating mentorship session:", getErrorMessage(error));
            console.log({networkResponse});
        } finally {
            setCreatingMentorshipSession(false);
        }
    };

    const verifyDiscountCode = async () => {
        const code = form.getFieldValue('discountCode');
        if (!code) return message.error('Please enter a discount code');

        setVerifyingDiscount(true);
        try {
            const response = await MentorshipAPI.verifyDiscountCode({ code }) as AxiosResponse<{discount_code: DiscountCode}>;
            if (response.data.discount_code) {
                if (response.data.discount_code.session) {
                    setNetworkResponse({title: "Discount code already used", type: "error"});
                    setDiscountVerified(false);
                } else {
                    setNetworkResponse({title: "Discount code verified successfully!", type: "success"});
                    setDiscountVerified(true);
                    setDiscountCode(response.data.discount_code);
                }
            } else {
                setNetworkResponse({title: "Invalid discount code", type: "error"});
                setDiscountVerified(false);
            }
        } catch (error: any) {
            setNetworkResponse({title: error.response?.data?.error || error.message || 'Verification failed', type: "error"});
            setDiscountVerified(false);
            console.error('Discount verification error:', error);
        } finally {
            setVerifyingDiscount(false);
        }
    };

    return (
        <Space direction="vertical" style={{ width: '100%' }}>
            <Text strong>Select a session:</Text>
            <Table
                dataSource={mentor.prices}
                rowKey={(record) => `${record.minutes}-${record.price}`}
                pagination={false}
                size="small"
                rowSelection={{
                    type: 'radio',
                    selectedRowKeys: selectedPrice ? [`${selectedPrice.minutes}-${selectedPrice.price}`] : [],
                    onSelect: (record: Duration) => {
                        setSelectedPrice(record);
                    }
                }}
                columns={[
                    {
                        title: 'Duration',
                        dataIndex: 'minutes',
                        key: 'minutes',
                        render: (minutes) => `${minutes} minutes`
                    },
                ]}
            />

            <Form
                form={form}
                layout="vertical"
                className="max-w-md"
                onValuesChange={handleValuesChange}
            >


                <Form.Item
                    name="discountCode"
                    label="Discount Code"
                    rules={[{ required: true, message: 'Please enter a discount code' }]}
                >
                    <Input placeholder="Enter discount code" />
                </Form.Item>
                <Form.Item>
                    <Button
                        type="primary"
                        onClick={verifyDiscountCode}
                        loading={verifyingDiscount}
                        disabled={discountVerified}
                    >
                        {discountVerified ? 'Verified' : 'Verify Discount Code'}
                    </Button>
                </Form.Item>
                <Form.Item>
                    {!isLoggedIn  &&
                        <Alert message="You must be logged in to create a mentorship session" type="warning" className='my-3' />
                    }
                    <Button
                        type="primary"
                        onClick={createMentorshipSession}
                        loading={creatingMentorshipSession}
                        disabled={!discountVerified || !isLoggedIn}
                    >
                        Create Mentorship Session
                    </Button>

                    <p className='text-muted'>
                        {discountVerified ? '' : 'Verify Discount Code before creating a session'}
                    </p>
                    
                </Form.Item>
            </Form>
            {networkResponse && (
                <Alert
                    message={networkResponse.title}
                    type={networkResponse.type}
                    showIcon
                    closable
                    onClose={() => setNetworkResponse(null)}
                />
            )}
        </Space>
    );
};

export default PaymentStep;
